import { FALLBACK_PARENT, isDeleted, type BookmarkChange, type BookmarkRow, type RejectReason } from '@bsync/shared';

/**
 * The per-field last-writer-wins merge rule. This is a line-by-line mirror of
 * `public.push_changes` in the SQL migration; the database test-suite runs
 * the same scenarios against both to keep them in lockstep.
 *
 * Properties:
 *  - Idempotent: re-applying a change is a no-op (strict `>` on clocks).
 *  - Commutative per field group: the result does not depend on arrival
 *    order, except for location changes rejected because they would create
 *    a cycle (the server is the single serialisation point for those).
 */
export interface MergeContext {
  rev: number;
  now: string;
  deviceId: string | null;
  /** Returns a reason if moving `id` under `parentId` is not allowed. */
  validateLocation?: (id: string, parentId: string, position: string) => RejectReason | null;
}

export interface MergeOutcome {
  row: BookmarkRow | undefined;
  changed: boolean;
  rejected?: RejectReason;
}

const MAX_TITLE = 1024;
const MAX_URL = 8192;

export function mergeChange(existing: BookmarkRow | undefined, change: BookmarkChange, ctx: MergeContext): MergeOutcome {
  let row: BookmarkRow;
  const isNew = !existing;
  if (!existing) {
    if (!change.kind) return { row: undefined, changed: false, rejected: 'missing_kind' };
    row = {
      id: change.id,
      kind: change.kind,
      parentId: FALLBACK_PARENT,
      position: 'a0',
      title: '',
      url: null,
      contentHlc: '',
      locationHlc: '',
      deleteFlag: false,
      deletedHlc: '',
      deleted: false,
      rev: ctx.rev,
      updatedAt: ctx.now,
      updatedBy: ctx.deviceId,
    };
  } else {
    if (change.kind && change.kind !== existing.kind) return { row: existing, changed: false, rejected: 'kind_mismatch' };
    row = { ...existing };
  }

  let changed = isNew;
  let rejected: RejectReason | undefined;

  if (change.content && change.content.hlc > row.contentHlc) {
    row.title = (change.content.title ?? '').slice(0, MAX_TITLE);
    row.url = row.kind === 'bookmark' ? (change.content.url ?? '').slice(0, MAX_URL) : null;
    row.contentHlc = change.content.hlc;
    changed = true;
  }

  if (change.location && change.location.hlc > row.locationHlc) {
    const { parentId, position, hlc } = change.location;
    const reason = ctx.validateLocation?.(row.id, parentId, position) ?? null;
    if (!reason) {
      row.parentId = parentId;
      row.position = position;
      row.locationHlc = hlc;
      changed = true;
    } else {
      rejected = reason;
      if (isNew) {
        // A brand-new node still needs a home: park it in Other bookmarks.
        row.parentId = FALLBACK_PARENT;
        row.position = reason === 'invalid_value' ? 'a0' : position;
        row.locationHlc = hlc;
      }
    }
  }

  if (change.deletion && change.deletion.hlc > row.deletedHlc) {
    row.deleteFlag = change.deletion.deleted;
    row.deletedHlc = change.deletion.hlc;
    changed = true;
  }

  row.deleted = isDeleted(row);
  if (changed && !isNew) {
    row.rev = ctx.rev;
    row.updatedAt = ctx.now;
    row.updatedBy = ctx.deviceId;
  }
  return { row, changed, rejected };
}

/** Merge a newer change into an older pending one for the same node. */
export function combineChanges(older: BookmarkChange, newer: BookmarkChange): BookmarkChange {
  return {
    id: older.id,
    kind: older.kind ?? newer.kind,
    content: pickNewer(older.content, newer.content),
    location: pickNewer(older.location, newer.location),
    deletion: pickNewer(older.deletion, newer.deletion),
  };
}

function pickNewer<T extends { hlc: string }>(a: T | undefined, b: T | undefined): T | undefined {
  if (!a) return b;
  if (!b) return a;
  return b.hlc >= a.hlc ? b : a;
}
